import { createAsyncThunk } from "@reduxjs/toolkit";

import * as basketAPI from "../../../api/basket";
import { IBasket } from "../../../models/catalogue";
import { IBasketLineAPIURL } from "../../../models/nominals";
import { loadBasket } from "./loadBasket";

/**
 * Arguments for updateBasketLineQuantity thunk.
 */
interface UpdateBasketLineQuantityArgs {
    lineURL: IBasketLineAPIURL;
    newQty: number;
}

/**
 * Update the quantity of a basket line.
 *
 * This thunk updates the quantity of an existing product in the basket
 * and reloads the basket to reflect pricing and offer changes. Used
 * primarily in the cart view for quantity adjustments.
 *
 * @param {IBasketLineAPIURL} args.lineURL - API URL of the basket line to update
 * @param {number} args.newQty - New quantity for the line
 * @returns {Promise<IBasket>} Promise resolving to the reloaded basket
 *
 * @example
 * // Update line quantity to 3
 * dispatch(updateBasketLineQuantity({
 *   lineURL: line.url,
 *   newQty: 3
 * }));
 *
 * @sideEffects
 * - Makes API call to update line quantity via basketAPI.updateLineQuantity
 * - Dispatches loadBasket thunk to reload basket data
 * - Updates `data.basket` with reloaded basket via extraReducers
 * - May trigger offer recalculations on the backend
 */
export const updateBasketLineQuantity = createAsyncThunk(
    "checkout/updateBasketLineQuantity",
    async (
        args: UpdateBasketLineQuantityArgs,
        { dispatch },
    ): Promise<IBasket> => {
        await basketAPI.updateLineQuantity(args.lineURL, args.newQty);
        const result = await dispatch(loadBasket()).unwrap();
        return result.basket;
    },
);
